/**
 * MusicLinkHelper.js - 音乐链接处理工具
 * 功能：提取、清理、验证 VV 音乐和 YouTube 链接
 */

export const extractMusicUrl = (input) => {
  if (!input || typeof input !== 'string') return input;  // 🔴 非 URL 保持原樣
  
  // 只有 YouTube 才需要提取
  if (input.includes('youtube.com') || input.includes('youtu.be')) {
    try {
      const urlMatch = input.match(/https?:\/\/[^\s]+/);
      if (!urlMatch) return input;
      let url = urlMatch[0].split('-')[0].trim();
      url = url.replace(/[\s\u3000]+$/, '');
      new URL(url);
      return url;
    } catch (error) {
      console.error('提取 YouTube URL 时出错:', error);
      return input;  // 🔴 出錯時保持原樣，不返回 null
    }
  }
  
  // 🔴 非 YouTube 的鏈結：完全保持原樣，不做任何處理
  return input;
};

export const extractMultipleUrls = (text) => {
  if (!text) return [];
  const lines = text.split('\n');
  const urls = [];
  for (const line of lines) {
    const url = extractMusicUrl(line);
    if (url && !urls.includes(url)) urls.push(url);
  }
  return urls;
};

export const detectMusicUrlType = (url) => {
  if (!url || typeof url !== 'string') return 'other';
  if (url.includes('b.51vv2.com') || url.includes('51vv2.com')) return 'vv';
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  return 'other';
};

export const isVVMusicUrl = (url) => detectMusicUrlType(url) === 'vv';
export const isYouTubeUrl = (url) => detectMusicUrlType(url) === 'youtube';

export const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  try {
    if (url.includes('youtube.com')) {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('v');
    }
    if (url.includes('youtu.be')) {
      return url.split('youtu.be/')[1].split('?')[0];
    }
    return null;
  } catch (error) {
    console.error('提取 YouTube ID 时出错:', error);
    return null;
  }
};

export const getYouTubeEmbedUrl = (url) => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}`;
};

export const validateMusicUrl = (url) => {
  if (!url) return { isValid: false, type: null, message: 'URL 为空' };
  try {
    new URL(url);
  } catch {
    return { isValid: false, type: null, message: '无效的 URL 格式' };
  }
  const type = detectMusicUrlType(url);
  if (type === 'youtube') {
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) return { isValid: false, type: 'youtube', message: '无法提取 YouTube ID' };
    return { isValid: true, type: 'youtube', message: 'YouTube 链接' };
  }
  // 🔴 其他链接都接受，保持原样
  return { isValid: true, type: 'other', message: '其他链接（保持原样）' };
};

export const formatUrlForDisplay = (url, maxLength = 50) => {
  if (!url) return '';
  if (url.length <= maxLength) return url;
  const start = url.substring(0, maxLength / 2);
  const end = url.substring(url.length - maxLength / 2);
  return `${start}...${end}`;
};

export const getPlayerConfig = (url) => {
  const type = detectMusicUrlType(url);
  if (type === 'vv') {
    return { playerType: 'iframe', src: url, width: '100%', height: 500 };
  } else if (type === 'youtube') {
    const embedUrl = getYouTubeEmbedUrl(url);
    if (!embedUrl) return { playerType: 'none', error: '无法获取 YouTube embed URL' };
    return { playerType: 'iframe', src: embedUrl, width: '100%', height: 500 };
  }
  return { playerType: 'none', error: '不支持的链接类型' };
};

export const prepareUrlForStorage = (input, metadata = '') => {
  const url = extractMusicUrl(input);
  if (!url) return null;
  const validation = validateMusicUrl(url);
  if (!validation.isValid) return null;
  return {
    url,
    type: validation.type,
    metadata,
    originalInput: input,
    timestamp: new Date().toISOString(),
    displayUrl: formatUrlForDisplay(url)
  };
};

const MusicLinkHelper = {
  extractMusicUrl,
  extractMultipleUrls,
  detectMusicUrlType,
  isVVMusicUrl,
  isYouTubeUrl,
  extractYouTubeVideoId,
  getYouTubeEmbedUrl,
  validateMusicUrl,
  formatUrlForDisplay,
  getPlayerConfig,
  prepareUrlForStorage
};

export default MusicLinkHelper;
